/**
 * Shared invoice reconciliation helpers — every place that records or edits a
 * payment against an invoice (PaymentForm.tsx, InvoiceDetail.tsx) should go
 * through applyPaymentToInvoice/reversePaymentFromInvoice so paidAmount and
 * status stay consistent everywhere instead of each screen re-deriving it.
 */
import { invoiceStore, Invoice } from '@/pages/Invoice/invoiceStore'

/** Recompute status from paidAmount vs totalAmount, without disturbing
 *  pre-collection states (draft/submitted/rejected/cancelled) that aren't
 *  driven by payment at all. */
export function deriveInvoiceStatus(current: Invoice['status'], totalAmount: number, paidAmount: number): Invoice['status'] {
  // totalAmount/paidAmount often arrive as Postgres `numeric` strings (e.g.
  // "1000.00") straight from the DB — coerce here so every caller gets correct
  // numeric comparisons instead of accidental string comparison.
  const total = Number(totalAmount) || 0
  const paid = Number(paidAmount) || 0
  if (current === 'cancelled') return current
  if (total > 0 && paid >= total) return 'paid'
  if (paid > 0) return 'partially_paid'
  if (current === 'draft' || current === 'submitted' || current === 'rejected') return current
  return 'issued'
}

/**
 * Apply `amount` of a payment against an invoice's collected total. Capped at
 * what's actually outstanding (an invoice can never be over-collected here,
 * regardless of what the caller passes). Returns the amount actually applied.
 */
export function applyPaymentToInvoice(invoiceId: string, amount: number): number {
  const inv = invoiceStore.get(invoiceId)
  if (!inv || !(amount > 0)) return 0
  // inv.totalAmount / inv.paidAmount are Postgres `numeric` columns and arrive
  // as strings (e.g. "1000.00") — `+`/`-` on those would concatenate instead
  // of adding/subtracting, so coerce before any arithmetic.
  const totalAmount = Number(inv.totalAmount) || 0
  const currentPaid = Number(inv.paidAmount) || 0
  const outstanding = Math.max(0, totalAmount - currentPaid)
  const applied = Math.min(amount, outstanding)
  if (applied <= 0) return 0
  const paidAmount = currentPaid + applied
  invoiceStore.save({ id: inv.id, paidAmount, status: deriveInvoiceStatus(inv.status, totalAmount, paidAmount) })
  return applied
}

/** Reverse a previously-applied amount (editing or voiding a payment). */
export function reversePaymentFromInvoice(invoiceId: string, amount: number): void {
  const inv = invoiceStore.get(invoiceId)
  if (!inv || !(amount > 0)) return
  const totalAmount = Number(inv.totalAmount) || 0
  const paidAmount = Math.max(0, (Number(inv.paidAmount) || 0) - amount)
  invoiceStore.save({ id: inv.id, paidAmount, status: deriveInvoiceStatus(inv.status, totalAmount, paidAmount) })
}

/** True if this invoice is unpaid/partially paid and past its due date —
 *  computed on the fly rather than relying on a persisted 'overdue' status,
 *  since nothing in the app ever transitions a record into that status. */
export function isInvoiceOverdue(inv: Pick<Invoice, 'status' | 'dueDate' | 'paidAmount' | 'totalAmount'>): boolean {
  if (inv.status === 'paid' || inv.status === 'cancelled' || inv.status === 'draft' || !inv.dueDate) return false
  return new Date(inv.dueDate) < new Date() && (Number(inv.paidAmount) || 0) < (Number(inv.totalAmount) || 0)
}
